import { ref, readonly, useContext, Ref } from "@nuxtjs/composition-api";
import type { UseCaptChaErrors, UseCaptChaInterface } from "./useCaptCha";
import api from "~/config/extensionsApi";
import { postApiAxios, getApiAxios } from "~/helpers";
import CryptoJS from "crypto-js";

export function useCaptCha(): UseCaptChaInterface {
  const { app } = useContext();
  const loading = ref(false);
  const error = ref<UseCaptChaErrors>({ getData: null, checkData: null });
  const captKey = ref<string>("");

  const resetPointAndKey = (point: { x: number; y: number }, captKey: Ref<string>) => {
    point.x = 0;
    point.y = 0;
    captKey.value = "";
  };

  const confirmEvent = async (
    point: { x: number; y: number },
    captKey: Ref<string>,
    mobileNumber: string,
    visitorId: string,
    emit: Function,
    sendNotification: Function,
    otpVerifyCaptcha: Function,
    result: any,
    loading: Ref<boolean>,
    captchaInstance: any
  ) => {
    loading.value = true;
    try {
      const response = await postApiAxios(
        api.otp.check_captcha,
        {
          point: [point.x, point.y].join(","),
          key: captKey.value,
        }
      );

      const data = response.result || {};
      if (data && (data["code"] || 0) === 0) {
        try {
          let key = CryptoJS.enc.Hex.parse(api.otp.crypto_key);
          let iv = CryptoJS.enc.Hex.parse(api.otp.crypto_iv);
          let encrypted = CryptoJS.AES.encrypt(mobileNumber, key, { iv: iv });

          encrypted = encrypted.ciphertext.toString(CryptoJS.enc.Base64);

          await otpVerifyCaptcha({
            mobileNumber: encrypted,
            visitorId: visitorId,
          });

          if (result.value.otpVerifyCaptchaResult && !loading.value) {
            emit("start-countdown");
            sendNotification({
              id: Symbol("verify_otp_success"),
              message: "Xác nhận thành công!",
              type: "success",
              icon: "check",
              persist: false,
              title: "Verify otp",
            });
            emit("update:isOtpSent", true);
            emit("update:isChooseOptionOtp", false);
            emit("update:hidePopupCaptcha", true);
          } else {
            emit("update:isOtpSent", false);
            emit("update:isChooseOptionOtp", false);
            emit("update:hidePopupCaptcha", false);
          }
        } catch (e) {
          console.warn(e);
        }
      } else {
        sendNotification({
          id: Symbol("verify_otp_success"),
          message: "Xác nhận thất bại!",
          type: "danger",
          icon: "check",
          persist: false,
          title: "Verify otp",
        });
        resetPointAndKey(point, captKey);
        captKey.value = await requestCaptchaData(captchaInstance, captKey);
      }
    } catch (e) {
      console.warn(e);
    } finally {
      loading.value = false;
    }
  };

  const requestCaptchaData = async (captchaInstance: any, captKey: Ref<string>) => {
    loading.value = true;
    if (captchaInstance) {
      captchaInstance.clear();
    }
    captKey.value = "";
    try {
      const response = await getApiAxios(api.otp.get_captcha);

      const data = response.result || {};

      if (data && (data["code"] || 0) === 0) {
        captchaInstance.setData({
          image: data["image_base64"] || "",
          thumb: data["tile_base64"] || "",
          thumbX: data["tile_x"] || 0,
          thumbY: data["tile_y"] || 0,
          thumbWidth: data["tile_width"] || 0,
          thumbHeight: data["tile_height"] || 0,
        });
        captKey.value = data["captcha_key"] || "";
      } else {
        error.value.getData = new Error("Unexpected authorization error. Check your Network connection.");
      }
    } catch (e) {
      console.warn(e);
    } finally {
      loading.value = false;
    }
    return captKey.value;
  };

  return {
    confirmEvent,
    requestCaptchaData,
    error: readonly(error),
    loading: readonly(loading),
    captKey,
  };
}

export default useCaptCha;
export * from "./useCaptCha";